import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { RefreshCw, Download, ArrowLeft, CheckCircle, FileText, Upload, X, Shield, Zap, Gauge, ChevronDown, ChevronUp } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Link } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import apiClient from '../lib/api';
import { getSessionId } from '../lib/session';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper function to get or create session ID
const CompressPdfPage = () => {
  const { toast } = useToast();
  const [uploadedPdf, setUploadedPdf] = useState(null);
  const [compressionLevel, setCompressionLevel] = useState([70]);
  const [compressionType, setCompressionType] = useState('balanced');
  const [compressLoading, setCompressLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [compressResult, setCompressResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [sessionId] = useState(getSessionId);
  const [openFaq, setOpenFaq] = useState(null);
  const [showBookmarkBanner, setShowBookmarkBanner] = useState(true);
  const fileInputRef = useRef(null);

  React.useEffect(() => {
    const bookmarkDismissed = localStorage.getItem('compress-pdf-bookmark-dismissed');
    if (bookmarkDismissed === 'true') {
      setShowBookmarkBanner(false);
    }
  }, []);

  const compressionLevels = {
    'maximum': { value: 90, description: 'Maximum compression, lower quality' },
    'balanced': { value: 70, description: 'Balanced compression and quality' },
    'minimum': { value: 50, description: 'Minimum compression, higher quality' }
  };

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Helper function to get PDF page count
  const getPdfPageCount = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      return pdf.numPages;
    } catch (error) {
      console.error('Error reading PDF:', error);
      return 0;
    }
  };

  const handleCompressionTypeChange = (type) => {
    setCompressionType(type);
    setCompressionLevel([compressionLevels[type].value]);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast({
        title: "Error",
        description: "Please upload a PDF file",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "File size too large. Maximum 10MB allowed",
        variant: "destructive",
      });
      return;
    }

    setUploadingFile(true);
    
    try {
      // Get actual page count from PDF
      const pageCount = await getPdfPageCount(file);

      // Upload to backend
      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await apiClient.post(
        `${BACKEND_URL}/api/pdf/upload?session_id=${sessionId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const pdfData = {
        id: uploadResponse.data.id,
        file: file,
        name: file.name,
        size: uploadResponse.data.size,
        pages: uploadResponse.data.pages,
        uploadDate: uploadResponse.data.upload_date,
        preview: URL.createObjectURL(file)
      };

      setUploadedPdf(pdfData);
      setCompressResult(null);
      
      toast({
        title: "Success",
        description: "PDF uploaded successfully",
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to upload PDF",
        variant: "destructive",
      });
    } finally {
      setUploadingFile(false);
    }
  };

  const removePdf = () => {
    if (uploadedPdf?.preview) {
      URL.revokeObjectURL(uploadedPdf.preview);
    }
    setUploadedPdf(null);
    setCompressResult(null);
  };

  const handleCompress = async () => {
    if (!uploadedPdf) {
      toast({
        title: "Error",
        description: "Please upload a PDF to compress",
        variant: "destructive",
      });
      return;
    }

    setCompressLoading(true);
    try {
      // Call backend compress API
      const response = await apiClient.post(
        `${BACKEND_URL}/api/pdf/compress?session_id=${sessionId}`,
        {
          pdf_id: uploadedPdf.id,
          compression_level: compressionLevel[0]
        }
      );
      
      // Calculate compression ratio
      const originalSizeStr = uploadedPdf.size;
      const compressedSizeStr = response.data.size;
      
      // Parse sizes to bytes for calculation
      const parseSize = (sizeStr) => {
        const match = sizeStr.match(/([\d.]+)\s*(KB|MB)/);
        if (!match) return 0;
        const value = parseFloat(match[1]);
        const unit = match[2];
        return unit === 'MB' ? value * 1024 * 1024 : value * 1024;
      };
      
      const originalBytes = parseSize(originalSizeStr);
      const compressedBytes = parseSize(compressedSizeStr);
      const compressionRatio = Math.round(((originalBytes - compressedBytes) / originalBytes) * 100);
      
      setCompressResult({
        ...response.data,
        originalSize: originalSizeStr,
        compressedSize: compressedSizeStr,
        compressionRatio: compressionRatio > 0 ? compressionRatio : 0
      });
      
      toast({
        title: "Success",
        description: "PDF compressed successfully",
      });
    } catch (error) {
      console.error('Compress error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to compress PDF",
        variant: "destructive",
      });
    } finally {
      setCompressLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!compressResult) return;
    
    try {
      const response = await apiClient.get(
        `${BACKEND_URL}${compressResult.download_url}?session_id=${sessionId}`,
        {
          responseType: 'blob',
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', compressResult.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: `Downloaded ${compressResult.name}`,
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to download file",
        variant: "destructive",
      });
    }
  };

  const handleDismissBookmarkBanner = () => {
    setShowBookmarkBanner(false);
    localStorage.setItem('compress-pdf-bookmark-dismissed', 'true');
  };

  return (
    <>
      <Helmet>
        <title>Compress PDF Online — Reduce PDF File Size Fast | AllSmallTooLS</title>
        <meta name="description" content="Shrink PDF file size without losing quality. Free online PDF compressor — secure, fast, and easy to use." />
        <meta name="keywords" content="compress pdf, reduce pdf size, pdf compressor online, shrink pdf, free pdf optimization" />
        <link rel="canonical" href="https://allsmalltools.com/compress-pdf" />
      </Helmet>

      <div className="space-y-6">
      {/* Bookmark Banner */}
      {showBookmarkBanner && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-indigo-200 rounded-2xl p-4 sm:p-6 shadow-lg">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-3 text-center sm:text-left">
              <div className="bg-indigo-100 p-3 rounded-full flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </div>
              <div>
                <p className="text-base sm:text-lg font-semibold text-gray-900">
                  Bookmark this page for quick access to PDF compressor anytime
                </p>
                <p className="text-sm text-gray-600 hidden sm:block">
                  Press Ctrl+D (Windows/Linux) or Cmd+D (Mac) to bookmark
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  const isMac = navigator.userAgent.toLowerCase().indexOf('mac') !== -1;
                  const shortcut = isMac ? 'Cmd+D' : 'Ctrl+D';
                  if (window.sidebar && window.sidebar.addPanel) {
                    window.sidebar.addPanel(document.title, window.location.href, '');
                  } else if (window.external && ('AddFavorite' in window.external)) {
                    window.external.AddFavorite(window.location.href, document.title);
                  } else {
                    alert(`To bookmark this page, press ${shortcut} on your keyboard.\n\nThis will save quick access to your PDF compressor for future use.`);
                  }
                }}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 flex items-center space-x-2 whitespace-nowrap"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
                <span>Bookmark Now</span>
              </button>

              <button
                onClick={handleDismissBookmarkBanner}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 hover:text-gray-900 p-3 rounded-lg transition-all duration-200 flex-shrink-0 shadow-sm hover:shadow-md"
                aria-label="Dismiss bookmark banner"
                title="Don't show again"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/">
          <Button variant="outline" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-2">
            <RefreshCw className="h-8 w-8 text-blue-500" />
            <span>Compress PDF</span>
          </h1>
          <p className="text-gray-600">Reduce PDF file size while maintaining quality</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Compression Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Compression Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* PDF Upload */}
            {!uploadedPdf ? (
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  uploadingFile ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                } ${
                  dragActive 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={uploadingFile ? undefined : handleDrag}
                onDragLeave={uploadingFile ? undefined : handleDrag}
                onDragOver={uploadingFile ? undefined : handleDrag}
                onDrop={uploadingFile ? undefined : handleDrop}
                onClick={uploadingFile ? undefined : () => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleFileInput}
                  className="hidden"
                  disabled={uploadingFile}
                />
                
                <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-colors ${
                  dragActive ? 'bg-blue-100' : 'bg-gray-100'
                }`}>
                  <Upload className={`h-8 w-8 ${dragActive ? 'text-blue-600' : 'text-gray-400'}`} />
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {uploadingFile ? 'Reading PDF...' : 'Upload PDF to Compress'}
                </h3>
                <p className="text-gray-600 mb-4">
                  {uploadingFile ? 'Please wait while we analyze your PDF' : 'Drag and drop your PDF file here, or click to browse'}
                </p>
                <p className="text-sm text-gray-500">
                  Maximum file size: 10MB • Supported format: PDF
                </p>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-16 bg-red-100 rounded flex items-center justify-center">
                    <FileText className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{uploadedPdf.name}</p>
                    <p className="text-sm text-gray-500">{uploadedPdf.pages} pages • {uploadedPdf.size}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={removePdf}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Compression Settings */}
            {uploadedPdf && (
              <div className="space-y-4">
                <Label>Compression Level</Label>
                <RadioGroup value={compressionType} onValueChange={handleCompressionTypeChange}>
                  {Object.entries(compressionLevels).map(([key, level]) => (
                    <div key={key} className="flex items-center space-x-2">
                      <RadioGroupItem value={key} id={key} />
                      <Label htmlFor={key} className="flex-1 cursor-pointer">
                        <div className="flex justify-between items-center">
                          <span className="capitalize font-medium">{key}</span>
                          <Badge variant="outline">{level.value}%</Badge>
                        </div>
                        <p className="text-sm text-gray-500">{level.description}</p>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>

                {/* Custom Compression Slider */}
                <div className="space-y-2">
                  <Label>Custom Compression: {compressionLevel[0]}%</Label>
                  <Slider
                    value={compressionLevel}
                    onValueChange={(value) => {
                      setCompressionLevel(value);
                      setCompressionType('custom');
                    }}
                    max={100}
                    min={10}
                    step={5}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Lower compression (larger file)</span>
                    <span>Higher compression (smaller file)</span>
                  </div>
                </div>
              </div>
            )}

            <Button 
              onClick={handleCompress}
              disabled={!uploadedPdf || compressLoading}
              className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
            >
              {compressLoading ? 'Compressing...' : 'Compress PDF'}
            </Button>
          </CardContent>
        </Card>

        {/* Results */}
        <Card>
          <CardHeader>
            <CardTitle>Compression Results</CardTitle>
          </CardHeader>
          <CardContent>
            {compressResult ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-green-800">Compression Complete!</span>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Original Size:</span>
                      <span className="font-medium">{compressResult.originalSize}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Compressed Size:</span>
                      <span className="font-medium text-green-600">{compressResult.compressedSize}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Size Reduction:</span>
                      <Badge className="bg-green-100 text-green-800">
                        {compressResult.compressionRatio}% smaller
                      </Badge>
                    </div>
                  </div>
                </div>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{compressResult.name}</p>
                        <p className="text-sm text-gray-500">Ready for download</p>
                      </div>
                      <Button
                        onClick={handleDownload}
                        className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <RefreshCw className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No compression performed yet</p>
                <p className="text-sm">Select a PDF and choose compression settings</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Why Use Our Compress PDF Tool Section */}
      <Card className="mt-12">
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-900">Why Use Our Compress PDF Tool?</h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Gauge className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Adjustable Compression</h3>
              <p className="text-gray-600">
                Choose your compression level from low to high. Balance file size reduction with quality preservation based on your needs. Perfect control over PDF optimization.
              </p>
            </div>
            
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-green-100 p-3 rounded-lg">
                <Shield className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Secure & Private</h3>
              <p className="text-gray-600">
                Your PDF files are processed securely with encryption. All uploaded and compressed files are automatically deleted after processing to protect your privacy.
              </p>
            </div>
            
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-orange-100 p-3 rounded-lg">
                <Zap className="h-6 w-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Fast Processing</h3>
              <p className="text-gray-600">
                Reduce PDF file size in seconds with our optimized compression algorithm. No software installation needed – compress PDFs directly in your browser.
              </p>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-gray-700">
              Whether you need to reduce PDF size for email attachments, web uploads, or storage optimization – our free PDF compressor makes it easy. Reduce file size by up to 90% while maintaining excellent quality. Try our compress PDF tool today!
            </p>
            <div className="mt-4 flex flex-wrap gap-4">
              <Link to="/split-pdf" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                → Split PDF Files
              </Link>
              <Link to="/merge-pdf" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                → Merge PDF Files
              </Link>
              <Link to="/lock-unlock-pdf" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                → Lock/Unlock PDF
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* How to Compress PDF Section */}
      <Card>
        <CardHeader>
          <h3 className="text-xl font-bold text-gray-900">How to Compress PDF Files Online</h3>
        </CardHeader>
        <CardContent>
          <ol className="space-y-4 text-gray-700">
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold">1</span>
              <div>
                <strong>Upload Your PDF:</strong> Click the upload area or drag and drop your PDF file. Our PDF compressor supports files of any size.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold">2</span>
              <div>
                <strong>Choose Compression Level:</strong> Select your preferred compression level – Low (maximum quality), Medium (balanced), or High (maximum compression). Use the slider for fine-tuning.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold">3</span>
              <div>
                <strong>Compress PDF:</strong> Click the "Compress PDF" button and wait while our tool optimizes your file. See the compression ratio and new file size.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold">4</span>
              <div>
                <strong>Download Compressed PDF:</strong> Download your optimized PDF file with reduced file size. Perfect for email, web upload, or storage.
              </div>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* FAQ Section */}
      <Card>
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 0 ? null : 0)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Is PDF compression free?</h3>
                {openFaq === 0 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 0 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Yes! Our PDF compressor is completely free to use. Compress unlimited PDF files without registration, watermarks, or hidden costs.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Will compression reduce PDF quality?</h3>
                {openFaq === 1 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 1 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">You control the quality! Choose low compression for minimal quality loss, medium for balanced results, or high compression for maximum file size reduction. Our tool optimizes while preserving readability.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">How much can I reduce PDF size?</h3>
                {openFaq === 2 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 2 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Compression results vary by file content. Typically, you can reduce PDF size by 30-90%. Files with many images compress more than text-only PDFs. Our tool shows exact file size reduction after compression.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 3 ? null : 3)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Is it safe to compress PDFs online?</h3>
                {openFaq === 3 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 3 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Absolutely! All files are processed over secure connections. We automatically delete uploaded and compressed files after processing. Your documents remain private and confidential.</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    </>
  );
};

export default CompressPdfPage;
